<template>
  <div class="modal checkout-confirm-modal" :class="{ 'is-active': value }">
    <div class="modal-background" @click.stop="close"></div>
    <div class="modal-content" style="max-width: 680px;">
      <div class="box modal-container">
        <h3 class="checkout-title">
          You're about to purchase {{ credits }} POP Credits.
        </h3>
        <div class="proceed-text">Do you want to proceed?</div>

        <!-- auto-refill panel (card checkout only; PayPal shows the order summary instead) -->
        <div v-show="paymentMethod !== 'paypal'" class="recurring" :class="{ on: autoRefill }">
          <div class="rec-head" @click.stop="toggleAutoRefill">
            <div class="rec-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></svg>
            </div>
            <div class="txt">
              <div class="h">
                Auto-refill these credits
                <span v-if="discountPercent > 0" class="rec-save">Save {{ discountPercent }}%</span>
                <span class="tag-off" :class="{ 'tag-on': autoRefill }">{{ autoRefill ? 'On' : 'Off' }}</span>
              </div>
              <div class="p">
                Top up automatically on a schedule so you never run out mid-project. No expiry, cancel anytime.
              </div>
            </div>
            <div class="switch"><div class="knob"></div></div>
          </div>

          <div class="rec-body">
            <div class="rec-body-inner">
              <div class="rec-body-pad">
                <div class="freq-label">How often should we refill?</div>
                <div class="freq">
                  <div v-for="months in intervals" :key="months"
                    class="freq-opt" :class="{ active: intervalMonths === months }"
                    @click.stop="intervalMonths = months">
                    {{ freqShort[months] }}
                    <small>
                      <s v-if="discountPercent > 0">${{ basePrice.toFixed(2) }}</s>
                      <b>${{ discountedPrice }}</b> / charge
                    </small>
                  </div>
                </div>

                <div class="rec-summary">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <div class="s-text">
                    We'll add <b>{{ credits }} POP Credits</b> to your account and charge
                    <b>${{ discountedPrice }}</b><span v-if="discountPercent > 0" class="off"> ({{ discountPercent }}% off)</span>
                    to your saved card <b>{{ freqLabel[intervalMonths] }}</b>.
                    First charge today, then again on <b>{{ nextChargeDate }}</b>. Add-on credits never expire.
                  </div>
                </div>

                <div v-if="hasPaymentSource === false" class="no-card-warning">
                  <div>Auto-refill needs a saved card.</div>
                  <v-btn small rounded color="profileAnchorColor" class="text-none whiteColor--text"
                    @click.stop="$emit('add-card')">
                    Add a card
                  </v-btn>
                </div>

                <div class="rec-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                  <span class="note-text">
                    Recurring purchases are billed to your saved credit card.
                    Manage or cancel anytime under
                  </span>
                  <span class="note-target"><b>Billing &amp; payments →<br/>Buy credits</b>.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- paypal checkout: no recurring, so show the order instead -->
        <div v-if="paymentMethod === 'paypal'" class="pp-sum">
          <div class="r">
            <span>{{ credits }} POP Credits</span>
            <b>${{ eachPrice }} each</b>
          </div>
          <div class="r total">
            <span>
              Total due today
              <small>One-time purchase · credits never expire</small>
            </span>
            <b>${{ payTotal }}</b>
          </div>
        </div>
        <div v-if="paymentMethod === 'paypal'" class="pp-swap">
          <div class="ic">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></svg>
          </div>
          <div>
            <div class="h">
              {{ discountPercent > 0 ? `Auto-refill saves ${discountPercent}% with a card` : 'Auto-refill is card only' }}
            </div>
            <div class="p">Recurring purchases need a saved credit card, so PayPal checkout is one-time only.</div>
            <button class="btn-link" @click.stop="$emit('switch-to-card')">Pay with credit card instead</button>
          </div>
        </div>

        <div v-if="errorMsg" class="redColor--text mt-3">{{ errorMsg }}</div>

        <div class="m-actions">
          <v-btn rounded outlined color="mainColor" class="button-with-sicon mainColor--text text-none"
            @click.stop="close">
            Cancel
          </v-btn>
          <!-- The PayPal iframe button is rendered into this container by the
               parent; v-show keeps the node in the DOM once rendered. -->
          <div v-show="paymentMethod === 'paypal'" id="modal-paypal-button" class="modal-paypal-button"></div>
          <v-btn v-show="paymentMethod !== 'paypal'" rounded color="profileAnchorColor"
            class="button-with-sicon whiteColor--text text-none ml-2"
            :disabled="confirmDisabled"
            @click.stop="confirm">
            {{ autoRefill ? 'Start auto-refill' : 'Yes' }}
          </v-btn>
        </div>
      </div>
      <button class="custom-modal-close-btn" aria-label="close" @click="close">
        <i class="material-icons">close</i>
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'CheckoutConfirmModal',
  props: {
    value: { type: Boolean, default: false },
    credits: { type: Number, default: 0 },
    basePrice: { type: Number, default: 0 }, // undiscounted one-time bundle price
    oneTimePrice: { type: Number, default: 0 }, // coupon-adjusted one-time price (PayPal order total)
    paymentMethod: { type: String, default: 'stripe' }, // stripe | paypal
    discountPercent: { type: Number, default: 0 },
    // Tri-state: null = not known yet (check still in flight or failed),
    // treated as "maybe"; the server-side 409 is the authoritative backstop.
    hasPaymentSource: { type: Boolean, default: null },
    errorMsg: { type: String, default: '' },
  },
  data() {
    return {
      autoRefill: false,
      intervalMonths: 1,
      intervals: [1, 2, 3],
      freqShort: { 1: 'Every month', 2: 'Every 2 months', 3: 'Every 3 months' },
      freqLabel: { 1: 'every month', 2: 'every 2 months', 3: 'every 3 months' },
    }
  },
  computed: {
    toggleDisabled() {
      return this.paymentMethod === 'paypal'
    },
    discountedPrice() {
      return (this.basePrice * (1 - this.discountPercent / 100)).toFixed(2)
    },
    eachPrice() {
      return this.credits > 0 ? (this.basePrice / this.credits).toFixed(2) : '0.00'
    },
    payTotal() {
      return (this.oneTimePrice || this.basePrice).toFixed(2)
    },
    nextChargeDate() {
      return moment().add(this.intervalMonths, 'months').format('MMM D, YYYY')
    },
    confirmDisabled() {
      return this.autoRefill && this.hasPaymentSource === false
    },
  },
  watch: {
    value(isShown) {
      if (isShown) {
        // Auto-refill is opt-in on every open; never persist a previous choice.
        this.autoRefill = false
        this.intervalMonths = 1
      }
    },
  },
  methods: {
    close() {
      this.$emit('input', false)
    },
    toggleAutoRefill() {
      if (this.toggleDisabled) {
        return
      }
      this.autoRefill = !this.autoRefill
    },
    confirm() {
      if (this.autoRefill) {
        this.$emit('confirm-auto-refill', { intervalMonths: this.intervalMonths })
      } else {
        this.$emit('confirm-one-time')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.checkout-title {
  font-size: 20px;
  font-weight: 800;
  color: var(--v-mainColor-base);
  line-height: 1.35;
  margin: 0 0 8px;
}
.proceed-text {
  font-size: 14.5px;
  line-height: 1.55;
  color: #6B7B7E;
}

.recurring {
  margin-top: 20px;
  border: 1.5px solid #E4E4E4;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color .2s, box-shadow .2s;
  &.on {
    border-color: #25A97A;
    box-shadow: 0 0 0 1px #25A97A inset;
  }
}
.rec-head {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 18px 20px;
  cursor: pointer;
  // named rec-icon (not .icon) to dodge Bulma's global .icon sizing
  .rec-icon {
    flex: 0 0 auto;
    width: 42px;
    height: 42px;
    border-radius: 6px;
    background: #D6FBEE;
    color: #25A97A;
    display: grid;
    place-items: center;
    svg {
      width: 24px;
      height: 24px;
      fill: none;
    }
  }
  .txt {
    flex: 1 1 auto;
    .h {
      font-size: 1.05rem;
      font-weight: 800;
      color: var(--v-mainColor-base);
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
    }
    .p {
      font-size: 13.5px;
      color: #6B7B7E;
      margin-top: 3px;
      max-width: 560px;
    }
  }
}
.rec-save {
  font-size: 0.7rem;
  font-weight: 800;
  color: var(--v-profileAnchorColor-base);
  background: #fff;
  border: 1.5px solid var(--v-profileAnchorColor-base);
  border-radius: 50vh;
  padding: 2px 9px;
  white-space: nowrap;
}
.tag-off {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #9AA6A8;
  background: #F7F7F7;
  border: 1px solid #E4E4E4;
  border-radius: 50vh;
  padding: 2px 9px;
  &.tag-on {
    color: #25A97A;
    background: #D6FBEE;
    border-color: transparent;
  }
}
.switch {
  flex: 0 0 auto;
  width: 50px;
  height: 28px;
  border-radius: 50vh;
  background: #B5B5B5;
  position: relative;
  transition: background .2s;
  margin-top: 4px;
  .knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, .25);
    transition: left .2s;
  }
}
.recurring.on .switch {
  background: #25A97A;
  .knob { left: 25px; }
}

.rec-body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows .25s ease;
}
.recurring.on .rec-body {
  grid-template-rows: 1fr;
}
.rec-body-inner {
  overflow: hidden;
}
.rec-body-pad {
  padding: 4px 20px 20px;
  border-top: 1px solid #E4E4E4;
}
// named freq-label (not .field-label) to dodge Bulma's right-aligned .field-label
.freq-label {
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--v-mainColor-base);
  text-align: left;
  margin: 16px 0 9px;
}
.freq {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.freq-opt {
  flex: 1 1 0;
  min-width: 120px;
  text-align: center;
  padding: 11px 12px;
  border: 1.5px solid #E4E4E4;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 700;
  color: var(--v-mainColor-base);
  background: #fff;
  transition: .15s;
  small {
    display: block;
    font-weight: 600;
    font-size: 0.75rem;
    color: #6B7B7E;
    margin-top: 2px;
    s {
      color: #9AA6A8;
      text-decoration-thickness: 1.5px;
      margin-right: 5px;
      font-weight: 600;
    }
    b {
      font-weight: 800;
      color: var(--v-mainColor-base);
    }
  }
  &.active {
    border-color: #25A97A;
    background: #D6FBEE;
    color: #25A97A;
    small { color: #25A97A; }
    small s { color: #25A97A; opacity: .65; }
    small b { color: #25A97A; }
  }
}
.rec-summary {
  margin-top: 16px;
  background: #D6FBEE;
  border-radius: 6px;
  padding: 14px 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  svg {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    color: #25A97A;
    margin-top: 1px;
  }
  .s-text {
    font-size: 0.9rem;
    color: #013840;
    line-height: 1.5;
    b { font-weight: 800; }
    .off { color: #25A97A; font-weight: 800; }
  }
}
.no-card-warning {
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: 6px;
  background: #FFF4E8;
  color: #7A4A12;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.rec-note {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 14px;
  font-size: 12.5px;
  color: #6B7B7E;
  svg { width: 15px; height: 15px; flex: 0 0 auto; }
  // sentence fills the left; the Billing & payments target sits on the right
  // as its own vertically-centered item, per the design
  .note-text {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
  }
  .note-target {
    max-width: 200px;
    flex: 0 0 auto;
    text-align: left;
  }
  b { color: var(--v-mainColor-base); }
}

/* paypal checkout state (per "Buy Credits - Recurring Purchase Update 1") */
.pp-sum {
  margin-top: 18px;
  border: 1px solid #E4E4E4;
  border-radius: 6px;
  overflow: hidden;
  .r {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 13px 18px;
    font-size: 14.5px;
    color: #6B7B7E;
    b {
      color: var(--v-mainColor-base);
      font-weight: 800;
    }
    small {
      display: block;
      font-size: 12.5px;
      font-weight: 600;
      color: #9AA6A8;
      margin-top: 2px;
    }
  }
  .r + .r {
    border-top: 1px solid #E4E4E4;
  }
  .r.total {
    background: #F7F7F7;
    b { font-size: 20px; }
  }
}
.pp-swap {
  margin-top: 14px;
  display: flex;
  gap: 13px;
  align-items: flex-start;
  background: #FFEDDD;
  border-radius: 6px;
  padding: 14px 16px;
  .ic {
    flex: 0 0 34px;
    height: 34px;
    border-radius: 9px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-profileAnchorColor-base);
    svg { width: 19px; height: 19px; }
  }
  .h {
    font-size: 14px;
    font-weight: 800;
    color: var(--v-mainColor-base);
  }
  .p {
    font-size: 13.5px;
    line-height: 1.5;
    color: #6B7B7E;
    margin-top: 2px;
  }
}
.btn-link {
  background: none;
  border: none;
  padding: 0;
  margin-top: 6px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 800;
  color: var(--v-profileAnchorColor-base);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover {
    color: #E64E00;
  }
}

.m-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
  margin-top: 22px;
}
.modal-paypal-button {
  min-width: 170px;
  position: relative;
  z-index: 1;
}
</style>
